"use client";

/**
 * useTaskCreations — a task's Creations: the deliverables it handed over.
 *
 * A creation is a `task_artifacts` row minted at either handover seam:
 * `source 'presented'` (a file, via present_output, announced by
 * TASK_ARTIFACT_REGISTERED) or `source 'studio'` (generated media, via the
 * media pipeline's completion write, announced by JOB_READY). Generated media
 * HAS no present_output seam — nothing lands in the workspace to present — so
 * a presented-only definition locked every generated image/video out of the
 * shelf and trail (founder, 08-08).
 * Thin view over the SAME `['task-artifacts', taskId]` cache the Computer
 * panel already holds — React Query dedupes the fetch, so no new request.
 * No task → []. Story shelf and trail cards both ride this one selector.
 */

import { useMemo } from "react";
import { useTaskArtifacts } from "@/components/studio-canvas/queries";
import type { TaskArtifact } from "@/components/studio-canvas/lineage";

/** A deliverable the task made for the user: a presented file OR a generated
 *  media artifact. Explicit two-value check so an unforeseen future source
 *  (imports, system rows) stays out until deliberately admitted. */
export function isCreation(a: TaskArtifact): boolean {
  return a.source === "presented" || a.source === "studio";
}

export function useTaskCreations(taskId: string | null): {
  creations: TaskArtifact[];
} {
  const { artifacts } = useTaskArtifacts(taskId);
  const creations = useMemo(() => {
    if (!taskId) return [];
    return artifacts
      .filter(isCreation)
      .slice()
      .sort((x, y) =>
        x.created_at < y.created_at ? 1 : x.created_at > y.created_at ? -1 : 0
      );
  }, [artifacts, taskId]);
  return { creations };
}
